// ---------------------------------------------------------------------------
// useTenantGroupRole — Fetches and mutates the tenant-level group role config.
//
// On mount, fetches GET /tenantgrouprole to get existing ACG IDs, then
// resolves each ACG's display name via GET /accesscontrolgroup/{id}.
// `save()` calls POST or PUT depending on whether a config already exists.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';

import { AppError } from '../../api/errors.js';
import type {
  AccessControlGroup,
  TenantGroupRoles,
} from '../../services/accessControlService.interface.js';
import type { UsageContextOverrides } from '../../services/usageContext.interface.js';

import type {
  UseTenantGroupRoleParams,
  UseTenantGroupRoleResult,
} from './useTenantGroupRole.interface.js';

export function useTenantGroupRole({
  service,
}: UseTenantGroupRoleParams): UseTenantGroupRoleResult {
  const [dataCreatorGroups, setDataCreatorGroups] = useState<
    AccessControlGroup[]
  >([]);
  const [dataUserGroups, setDataUserGroups] = useState<AccessControlGroup[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AppError | null>(null);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<AppError | null>(null);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    async function load() {
      try {
        const tenantRole = await service.getTenantGroupRole();
        if (cancelled) return;

        setHasExistingConfig(true);

        // Resolve ACG display names for each ID in parallel.
        const [creatorAcgs, userAcgs] = await Promise.all([
          resolveAcgs(tenantRole.roles.dataCreator, service),
          resolveAcgs(tenantRole.roles.dataUser, service),
        ]);

        if (!cancelled) {
          setDataCreatorGroups(creatorAcgs);
          setDataUserGroups(userAcgs);
        }
      } catch (err) {
        if (cancelled) return;
        // 404 means no tenant group role has been created yet — treat as empty.
        if (err instanceof AppError && err.statusCode === 404) {
          setHasExistingConfig(false);
          setDataCreatorGroups([]);
          setDataUserGroups([]);
        } else {
          setError(
            err instanceof AppError
              ? err
              : new AppError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to load tenant roles',
                  'LOAD_ERROR',
                ),
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [service]);

  const save = useCallback(
    async (roles: TenantGroupRoles, usage?: UsageContextOverrides) => {
      setIsSaving(true);
      setSaveError(null);
      try {
        if (hasExistingConfig) {
          await service.updateTenantGroupRole({ roles }, usage);
        } else {
          await service.createTenantGroupRole({ roles }, usage);
          setHasExistingConfig(true);
        }
      } catch (err) {
        const appError =
          err instanceof AppError
            ? err
            : new AppError(
                err instanceof Error ? err.message : 'Failed to save roles',
                'SAVE_ERROR',
              );
        setSaveError(appError);
        throw appError;
      } finally {
        setIsSaving(false);
      }
    },
    [service, hasExistingConfig],
  );

  return {
    dataCreatorGroups,
    dataUserGroups,
    isLoading,
    error,
    save,
    isSaving,
    saveError,
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

async function resolveAcgs(
  ids: string[],
  service: UseTenantGroupRoleParams['service'],
): Promise<AccessControlGroup[]> {
  const results = await Promise.allSettled(
    ids.map((id) => service.getAccessControlGroup(id)),
  );
  return results
    .filter(
      (r): r is PromiseFulfilledResult<AccessControlGroup> =>
        r.status === 'fulfilled',
    )
    .map((r) => r.value);
}
